import { type QueryClient, queryOptions, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SearchBenchmarksQueryVariables } from '@vizit/api';
import { isBenchmarksUnavailableError, useSearchBenchmarksQuery } from '@vizit/api';
import { FEATURE_FLAGS } from '@vizit/constants';
import { useFeatureFlag } from './use-feature-flag';

interface CustomBenchmarksAvailability {
  /** The flag is on, a workspace is given, and the API has not said benchmarks are off for its organisation. */
  isEnabled: boolean;
  /** The flag is on and the API check for the workspace has not answered yet. */
  isChecking: boolean;
}

/**
 * How long to wait before each repeat of a "benchmarks are off" answer. The API answers off for a
 * moment whenever its LaunchDarkly client is not ready yet, so the answer is confirmed over about
 * 3 seconds before the Benchmarks view is hidden.
 */
function unavailableRetryDelays() {
  // Unit tests never retry, the repo-wide idiom: suites share one QueryClient under fake timers.
  if (import.meta.env.VITEST) {
    return [];
  }
  return [1000, 2000];
}

/**
 * How long an answer is kept before the next mount asks again. Asking again happens behind the
 * kept answer, so pages do not wait on it; an organisation that gets benchmarks turned on sees
 * them on the next page after this, or on reload.
 */
const AVAILABILITY_STALE_TIME = 60 * 1000;

/**
 * The API half of the availability check: `true` once the workspace's benchmark list loads, `false` once the API has
 * said benchmarks are off for the organisation, confirmed over the retry delays. Shared by
 * `useCustomBenchmarksAvailability` and the route guards, so either one's answer serves the other from the cache and
 * both ask again after the same stale time.
 */
function customBenchmarksAvailabilityQueryOptions(queryClient: QueryClient, workspaceId: string) {
  return queryOptions({
    queryKey: ['CustomBenchmarksAvailability', workspaceId],
    queryFn: async ({ queryKey }) => {
      const variables: SearchBenchmarksQueryVariables = {
        workspace_id: workspaceId,
        sort_by: 'created_at',
        sort_direction: 'desc',
      };
      const keptAnswer = queryClient.getQueryData<boolean>(queryKey);
      const isAvailable = async () => {
        try {
          await queryClient.fetchQuery({
            queryKey: useSearchBenchmarksQuery.getKey(variables),
            queryFn: useSearchBenchmarksQuery.fetcher(variables),
            // The list may be cached as fresh for minutes; a re-check has to ask the API.
            staleTime: 0,
            retry: false,
          });
        } catch (error) {
          if (isBenchmarksUnavailableError(error)) {
            return false;
          }
          // Any other failure says nothing about the organisation, so a re-check keeps the answer it was checking.
          return keptAnswer ?? true;
        }
        return true;
      };

      // Reading the abort `signal` would cancel the check whenever its last page unmounts, restarting the wait on
      // the next page; the query's own state says whether it was cancelled or removed instead.
      const isStillChecking = () => queryClient.getQueryState(queryKey)?.fetchStatus === 'fetching';

      let available = await isAvailable();
      for (const delay of unavailableRetryDelays()) {
        if (available) {
          break;
        }
        await new Promise((resolve) => setTimeout(resolve, delay));
        if (!isStillChecking()) {
          break;
        }
        available = await isAvailable();
      }
      return available;
    },
    staleTime: AVAILABILITY_STALE_TIME,
    gcTime: Number.POSITIVE_INFINITY,
  });
}

/**
 * Whether custom benchmarks are available in a workspace. The flag is set per
 * environment, while the API enables benchmarks per organisation and answers
 * 404 `NOT_FOUND` on the benchmark routes elsewhere. With a `workspaceId`, the
 * workspace's benchmark list is fetched through the cache: it is the Benchmarks
 * view's default query, so the view and its count reuse the answer. Any other
 * failure on a first check counts as available, leaving the view to show its
 * error and retry; on a re-check it keeps the answer already given.
 * Without a `workspaceId` nothing confirms the organisation has them, so they are
 * off. Surfaces keep the benchmark UI and the scores it decides back while
 * `isChecking`, so neither shows and then swaps. The one swap left is a re-check
 * that finds the organisation's answer changed, which updates the page in place.
 *
 * The answer is its own query so it holds while the list refetches; reading the
 * list's status directly would flip back to pending on every refetch. An "off"
 * answer is asked again twice before it is kept, so only the first page in an
 * organisation without benchmarks waits on the check. Answers are kept for the
 * session, even while no page asks, so time spent elsewhere does not bring the
 * wait back.
 */
function useCustomBenchmarksAvailability(workspaceId: string | null | undefined): CustomBenchmarksAvailability {
  const isFlagOn = useFeatureFlag(FEATURE_FLAGS.CUSTOM_BENCHMARKS_FE, false);
  const shouldCheck = isFlagOn && !!workspaceId;
  const queryClient = useQueryClient();

  const { data, isPending } = useQuery({
    ...customBenchmarksAvailabilityQueryOptions(queryClient, workspaceId ?? ''),
    enabled: shouldCheck,
  });

  if (!shouldCheck) {
    return { isEnabled: false, isChecking: false };
  }

  return { isEnabled: data === true, isChecking: isPending };
}

export { customBenchmarksAvailabilityQueryOptions, useCustomBenchmarksAvailability };
export type { CustomBenchmarksAvailability };
